// Vendor Fonts
import "./assets/vendor/fonts/boxicons.css";

// Core CSS
import "./assets/vendor/css/core.css";
import "./assets/vendor/css/theme-default.css";
import "./assets/css/demo.css";

// Vendors CSS
import "./assets/vendor/libs/perfect-scrollbar/perfect-scrollbar.css";
import "./assets/vendor/libs/apex-charts/apex-charts.css";
import Footer from "./modules/core/components/footer/Footer";
import TopMenuBar from "./modules/core/components/TopMenuBar/TopMenuBar";
import SubscriptionBanner from "./modules/core/components/Subscription/SubscriptionBanner";

import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, Suspense, lazy, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GenerateRoutes, getNavigationItems } from "./utils/routeGenerator";
import PrivateRoute from "./modules/auth/PrivateRoute";
import { useCheckTokenExpiration } from "./utility/authGuard";
import { logout } from "./redux/features/auth.features";
import WhatsNewModal from "./modules/core/components/Brand/WhatsNewModal";
import SmartHolidaySystem from "./modules/core/components/Effects/SmartHolidaySystem";
import NetworkPingWidget from "./modules/core/components/Effects/NetworkPingWidget";
import { SessionProvider, useSession } from "./modules/core/context/SessionContext";
import { Modal, Button } from "react-bootstrap";
import { FaClock, FaExclamationTriangle } from "react-icons/fa";
import apiService from "./services/apiServices";
const BrandLoader = lazy(() => import("./modules/core/components/Brand/BrandLoader"));
const QuotaRequestModal = lazy(() => import("./modules/core/shared/components/QuotaRequestModal"));

const SystemSetupWizard = lazy(() => import("./modules/setup/SystemSetupWizard"));
const SetupGuard = lazy(() => import("./modules/setup/SetupGuard"));
const LoginForm = lazy(() => import("./modules/auth/Login"));
const BarcodePrinter = lazy(() => import("./modules/barcode/BarcodePrinter"));


const AppContent = () => {
  const { user } = useSelector(state => state.authReducer);
  const { profile } = useSelector(state => state.companyReducer || {});
  const { isExpiringSoon, timeLeft, formatTime } = useSession();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleReLogin = () => {
    dispatch(logout()).then(() => {
      window.location.href = '/login';
    });
  };

  const userName = user?.employeeId?.name || user?.username || 'there';
  const isLoginPage = location.pathname === '/login';
  const showLayout = user && !isLoginPage;

  // Get user permissions for route generation
  const userPermissions = useMemo(() => user && user.roles.map(role => role.permissions), [user]);
  const routes = useMemo(() => GenerateRoutes(userPermissions), [userPermissions]);
  const navigationItems = useMemo(() => getNavigationItems(routes), [routes]);

  const [quota, setQuota] = useState(null);

  useEffect(() => {
    if (user) {
      const fetchQuota = async () => {
        try {
          const res = await apiService.get('/admin/quota/my-usage');
          setQuota(res.data);
        } catch (err) {
          console.error("Quota fetch fail", err);
        }
      };
      fetchQuota();
    }
  }, [user]);

  useEffect(() => {
    const handleFocus = (event) => {
      const target = event.target
      if (target.tagName === "INPUT") {
        target.select()
      }
    }
    document.body.addEventListener('focus', handleFocus, true)
    return () => document.body.removeEventListener('focus', handleFocus, true)
  }, [])

  return (
    <div className="app">
      <SubscriptionBanner quota={quota} />
      <Suspense fallback={null}>
        {user && (
          <SmartHolidaySystem
            userJoinedDate={profile?.createdAt || user?.createdAt}
            userName={userName}
          />
        )}
      </Suspense>
      {user && <WhatsNewModal />}
      <Suspense fallback={null}>
        <QuotaRequestModal />
      </Suspense>
      <NetworkPingWidget />

      {/* PUBLIC ROUTES */}
      <Suspense fallback={<div className="d-flex justify-content-center align-items-center vh-100"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>}>
        <Routes>
          <Route path="/system-setup" element={<SetupGuard><SystemSetupWizard /></SetupGuard>} />
          <Route path="/login" element={isLoginPage ? <LoginForm /> : <Navigate to="/" replace />} />

          <Route path="*" element={null} />
        </Routes>
      </Suspense>

      {/* PROTECTED ROUTES */}
      {!isLoginPage && location.pathname !== '/system-setup' && (
        <div className={showLayout ? "layout-wrapper layout-top-menu" : "auth-wrapper"}>
          <div className={showLayout ? "layout-container" : "auth-container"}>
            <div className={showLayout ? "layout-page" : "auth-page"}>
              <div className={showLayout ? "content-wrapper" : ""}>
                {showLayout && <TopMenuBar navigationItems={navigationItems} />}
                <Routes>
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                  {/* Dynamically generated routes from ROUTE_CONFIG */}
                  {routes.map(route => (
                    <Route key={route.path} path={route.path} element={route.element} />
                  ))}
                  {/* Explicit LabelPrint route for reliability and to stop route-mismatch loops */}
                  <Route path="/LabelPrint" element={
                    <PrivateRoute requiredModule="barcode" requiredAction="view">
                      <Suspense fallback={null}>
                        <BarcodePrinter />
                      </Suspense>
                    </PrivateRoute>
                  } />
                  <Route path="*" element={
                    !user ? <Navigate to="/login" replace /> : (
                      <div className="container">
                        <div className="container-p-y">
                          <div className="misc-wrapper">
                            <h2 className="mb-2 mx-2">Page Not Found :(</h2>
                            <p className="mb-4 mx-2">Oops! 😖 The requested URL was not found on this server.</p>
                            <a href="/" className="btn btn-primary me-2 ">Back to home</a>
                            <a href="/login" className="btn btn-success">Login Again</a>
                          </div>
                        </div>
                      </div>
                    )
                  } />
                </Routes>
                {showLayout && <Footer />}
                <div className="content-backdrop fade" />
              </div>
            </div>
          </div>
          <div className="layout-overlay layout-menu-toggle" />
        </div>
      )}

      {/* Session Expiration Warning Modal */}
      <Modal show={isExpiringSoon} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center p-4">
          <div className="text-warning mb-3">
            <FaExclamationTriangle size={48} />
          </div>
          <h4 className="fw-bold">Session Expiring Soon</h4>
          <p className="text-muted">
            Your security session will end in <strong className="text-danger">{formatTime(timeLeft)}</strong>.
            Please re-login to continue working safely.
          </p>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button variant="outline-secondary" onClick={() => window.location.reload()}>
              Verify Session
            </Button>
            <Button variant="primary" onClick={handleReLogin}>
              Re-Login Now
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

function App() {
  useEffect(() => {
    const handleFocus = (event) => {
      const target = event.target
      if (target.tagName === "INPUT") {
        target.select()
      }
    }
    document.body.addEventListener('focus', handleFocus, true)
    return () => document.body.removeEventListener('focus', handleFocus, true)
  }, [])

  // Get user permissions and company data from Redux
  const { user } = useSelector(state => state.authReducer);
  const checkTokenExpiration = useCheckTokenExpiration();

  useEffect(() => {
    const publicPaths = ['/login', '/system-setup'];
    if (!checkTokenExpiration()) {
      if (!publicPaths.includes(window.location.pathname)) {
        window.location.href = '/login'
      }
    }
  }, [checkTokenExpiration])

  return (
    <SessionProvider>
      <AppContent />
    </SessionProvider>
  );
}

export default App;
